import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, Modal, ModalContent, ModalHeader } from "semantic-ui-react";

import { AddServicePendingChecklist, SortableList } from "modules/PendingChecklistsDashboard/components/ServiceChecklists/components/ServiceChecklistModal/components";
import "modules/PendingChecklistsDashboard/components/ServiceChecklists/components/ServiceChecklistModal/ServiceChecklistModal.scss";

import type { UniqueIdentifier } from "@dnd-kit/core";
import type { CheckInServicePendingChecklist, Checklist } from "models";
import type { ServicePendingChecklistsGrouped } from "modules/PendingChecklistsDashboard/components/ServiceChecklists/hooks";

export type SortableServicePendingChecklist = { id: UniqueIdentifier } & CheckInServicePendingChecklist;

type ServiceChecklistModalProps = {
  checkInServiceId: number;
  onClose: () => void;
  checklists?: Checklist[];
  servicePendingChecklistsGrouped?: ServicePendingChecklistsGrouped[];
};

export const ServiceChecklistModal = ({ onClose, checkInServiceId, checklists, servicePendingChecklistsGrouped }: ServiceChecklistModalProps) => {
  const { t } = useTranslation();

  const [sortableServicePendingChecklists, setSortableServicePendingChecklists] = useState<SortableServicePendingChecklist[]>([]);

  const selectedServiceDetails = servicePendingChecklistsGrouped?.find(({ checkInServiceId: serviceId }) => serviceId === checkInServiceId);

  useEffect(() => {
    const selectedServiceChecklists = selectedServiceDetails?.checklists ?? [];

    if (selectedServiceChecklists.length) {
      const offset = selectedServiceChecklists.length;
      setSortableServicePendingChecklists(selectedServiceChecklists.map(sc => ({ ...sc, id: offset + sc.ordering })));
      return;
    }

    setSortableServicePendingChecklists([]);
  }, [checkInServiceId, selectedServiceDetails]);

  return (
    <Modal open={Boolean(checkInServiceId)} closeOnDimmerClick={false} onClose={onClose} className="ServiceChecklistModal">
      <ModalHeader className="header-container">
        {t("v8_add_checklists_to_service", "Add Checklists to {{service}} Service", { service: selectedServiceDetails?.name || "" })}
        <div className="close-btn">
          <Button className="modal-close-btn" basic onClick={onClose}>
            <Icon className="xmark grey" />
          </Button>
        </div>
      </ModalHeader>
      <ModalContent>
        <div className="sortable-list-container">
          <SortableList
            checklists={checklists}
            sortableServicePendingChecklists={sortableServicePendingChecklists}
            setSortableServicePendingChecklists={setSortableServicePendingChecklists}
          />
        </div>
        <AddServicePendingChecklist
          checklists={checklists}
          checkInServiceId={checkInServiceId}
          items={sortableServicePendingChecklists}
          nextOrdering={sortableServicePendingChecklists.length + 1}
        />
      </ModalContent>
    </Modal>
  );
};
